import type { AuthSession } from './types';

/**
 * Magazyn sesji trzymany POZA Reactem.
 *
 * Dlaczego nie zwykly `useState`: tokenow potrzebuje rowniez klient HTTP,
 * ktory nie jest komponentem. Gdyby czytal je przez domkniecie, po odswiezeniu
 * tokena wysylalby stara wartosc. Tutaj zrodlo prawdy jest jedno, a React
 * subskrybuje je przez `useSyncExternalStore`.
 *
 * BEZPIECZENSTWO: tokeny leza w `localStorage`/`sessionStorage`, bo backend
 * zwraca je w ciele odpowiedzi i nie ustawia ciasteczka httpOnly. To standardowe
 * rozwiazanie dla takiego kontraktu, ale oznacza, ze skuteczny atak XSS daje
 * dostep do tokenow. Dlatego kazdy tekst wstawiany do DOM idzie przez React,
 * a nie przez `dangerouslySetInnerHTML`.
 */

const STORAGE_KEY = 'tenify.session';

type Listener = () => void;

const listeners = new Set<Listener>();
let current: AuthSession | null = null;

function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function parseSession(raw: string | null, persistent: boolean): AuthSession | null {
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const record = parsed as Record<string, unknown>;
    if (typeof record.accessToken !== 'string' || typeof record.refreshToken !== 'string') {
      return null;
    }

    return {
      accessToken: record.accessToken,
      refreshToken: record.refreshToken,
      sessionId: typeof record.sessionId === 'string' ? record.sessionId : null,
      persistent,
    };
  } catch {
    return null;
  }
}

/** Wczytuje sesje zapisana w przegladarce. `localStorage` ma pierwszenstwo. */
function loadFromStorage(): AuthSession | null {
  const persisted = parseSession(safeGetItem(localStorage, STORAGE_KEY), true);
  if (persisted) return persisted;
  return parseSession(safeGetItem(sessionStorage, STORAGE_KEY), false);
}

function writeToStorage(session: AuthSession | null): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);

    if (session) {
      const target = session.persistent ? localStorage : sessionStorage;
      target.setItem(
        STORAGE_KEY,
        JSON.stringify({
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          sessionId: session.sessionId,
        }),
      );
    }
  } catch {
    // Sesja zostaje wtedy tylko w pamieci — aplikacja dziala do przeladowania strony.
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

current = loadFromStorage();

/** Zwraca aktualna sesje. Referencja zmienia sie tylko przy realnej zmianie stanu. */
export function getSession(): AuthSession | null {
  return current;
}

/** Zapisuje sesje w pamieci i w przegladarce, po czym powiadamia subskrybentow. */
export function setSession(session: AuthSession | null): void {
  current = session;
  writeToStorage(session);
  emit();
}

/**
 * Podmienia tokeny zachowujac `sessionId` i tryb trwalosci.
 * Uzywane po odswiezeniu tokena — backend nie zwraca wtedy `sessionId`.
 */
export function updateTokens(accessToken: string, refreshToken: string): void {
  if (!current) return;
  setSession({ ...current, accessToken, refreshToken });
}

export function clearSession(): void {
  setSession(null);
}

/** Subskrypcja dla `useSyncExternalStore`. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Synchronizuje wylogowanie miedzy kartami przegladarki: gdy w innej karcie
 * zniknie wpis sesji, ta karta rowniez traci sesje.
 */
export function startCrossTabSync(): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;

    const next = loadFromStorage();
    const changed =
      next?.accessToken !== current?.accessToken || next?.refreshToken !== current?.refreshToken;

    if (changed) {
      current = next;
      emit();
    }
  };

  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('storage', handler);
  };
}
